import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import type { FormInstance, MenuProps } from "antd";
import { Button, Form, Input, Select } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import type { ResultParamField } from "../constants";
import {
  paramFieldRules,
  positiveAmountCrossFieldValidatePaths,
  type PositiveAmountContext,
  type PercentOfTriggerContext,
} from "../resultParamRules";
import type { WizardFormValues } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import SportsbookRestrictionSearchField from "./SportsbookRestrictionSearchField";
import styles from "../stylesDynamicBonuses.module.css";

const hasParamValue = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== "";

type Props = {
  form: FormInstance<WizardFormValues>;
  /** Form.List step field name (step index). */
  stepFieldName: number;
  /** Optional result params authored as chips (Add Restriction). */
  params: ResultParamField[];
  /** BONENG-913 — forwarded into param validators when percent sizing is active. */
  percentContext?: PercentOfTriggerContext;
  /** Result type key (e.g. "award_free_picks") — enables cross-field validation. */
  resultTypeKey?: string;
  onRestrictionDisplayName?: (id: string, name: string) => void;
};

/**
 * Chip-based authoring for optional result params. Unused params are chosen
 * via Add Restriction; set values render as removable chips. Values still
 * live on `steps[n].result_params` for submit/validation.
 *
 * BONENG-980: params with `sportsbookSearchType` also live behind Add
 * Restriction but render as searchable dropdowns with chips when active.
 */
const ShowBonusTargetingField: React.FC<Props> = ({
  form,
  stepFieldName,
  params,
  percentContext,
  resultTypeKey,
  onRestrictionDisplayName,
}) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [activeSportsbookKeys, setActiveSportsbookKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const chipOnlyParams = useMemo(
    () => params.filter((p) => !p.sportsbookSearchType),
    [params],
  );

  const rawResultParams = Form.useWatch(["steps", stepFieldName, "result_params"], {
    form,
    preserve: true,
  }) as Record<string, unknown> | undefined;
  const resultParams = useMemo(
    () => rawResultParams ?? {},
    [rawResultParams],
  );

  const firstTournamentId = useMemo(() => {
    const csv = resultParams.promo_tournament_ids;
    if (!csv || typeof csv !== "string") return undefined;
    return csv.split(",").map((s) => s.trim()).filter(Boolean)[0] || undefined;
  }, [resultParams.promo_tournament_ids]);

  const tournamentCount = useMemo(() => {
    const csv = resultParams.promo_tournament_ids;
    if (!csv || typeof csv !== "string") return 0;
    return csv.split(",").map((s) => s.trim()).filter(Boolean).length;
  }, [resultParams.promo_tournament_ids]);

  const matchIsShowing = useMemo(
    () =>
      activeSportsbookKeys.has("promo_match_ids") ||
      hasParamValue(resultParams.promo_match_ids),
    [activeSportsbookKeys, resultParams.promo_match_ids],
  );

  /** Sportsbook params that are visible — either explicitly opened or already have values.
   *  Match IDs always brings Tournament IDs along so the required first step is on screen. */
  const activeSportsbookParams = useMemo(
    () =>
      params.filter((p) => {
        if (!p.sportsbookSearchType) return false;
        if (p.key === "promo_tournament_ids" && matchIsShowing) return true;
        return activeSportsbookKeys.has(p.key) || hasParamValue(resultParams[p.key]);
      }),
    [params, activeSportsbookKeys, resultParams, matchIsShowing],
  );

  const positiveAmountContext: PositiveAmountContext | undefined = useMemo(() => {
    const TYPES_WITH_FLOOR = ["award_free_picks", "show_bonus", "create_profit_boost_promotion"];
    if (!resultTypeKey || !TYPES_WITH_FLOOR.includes(resultTypeKey)) return undefined;

    const percentSet = Boolean(resultParams.percent_of_trigger);

    let perPickAmount: number | null = null;
    if (resultTypeKey === "award_free_picks") {
      const amount = Number(resultParams.amount);
      const split = Number(resultParams.split_count) || 1;
      perPickAmount =
        Number.isFinite(amount) && amount > 0
          ? Math.floor((amount / split) * 100) / 100
          : null;
    }

    let maxWagerLimit: number | null = null;
    if (resultTypeKey === "show_bonus" || resultTypeKey === "create_profit_boost_promotion") {
      const cap = Number(resultParams.max_wager_limit);
      maxWagerLimit = Number.isFinite(cap) && cap > 0 ? cap : null;
    }

    return { perPickAmount, maxWagerLimit, percentOfTrigger: percentSet };
  }, [
    resultTypeKey,
    resultParams.amount,
    resultParams.split_count,
    resultParams.percent_of_trigger,
    resultParams.max_wager_limit,
  ]);

  const setParams = useMemo(
    () =>
      chipOnlyParams.filter(
        (param) =>
          param.key !== editingKey && hasParamValue(resultParams[param.key]),
      ),
    [chipOnlyParams, resultParams, editingKey],
  );

  const availableParams = useMemo(
    () =>
      params.filter((param) => {
        if (param.key === editingKey) return false;
        if (activeSportsbookKeys.has(param.key) || hasParamValue(resultParams[param.key])) {
          return false;
        }
        if (param.key === "promo_tournament_ids" && matchIsShowing) return false;
        return true;
      }),
    [editingKey, activeSportsbookKeys, params, resultParams, matchIsShowing],
  );

  const editingParam = chipOnlyParams.find((param) => param.key === editingKey);

  const revalidatePositiveAmountCrossFields = useCallback(async () => {
    const paths = positiveAmountCrossFieldValidatePaths(
      stepFieldName,
      resultTypeKey,
      resultParams,
    );
    if (paths.length === 0) return;
    try {
      await form.validateFields(paths);
    } catch {
      // Expected when the committed floor exceeds the cap / per-pick award.
    }
  }, [form, resultParams, resultTypeKey, stepFieldName]);

  const clearParam = useCallback(
    (key: string) => {
      const next = { ...form.getFieldValue(["steps", stepFieldName, "result_params"]) };
      const removedTournamentWithValue =
        key === "promo_tournament_ids" && hasParamValue(next[key]);
      delete next[key];
      // Only drop matches when a real tournament selection is removed.
      // An empty companion Tournament IDs field must not wipe hydrated Match IDs.
      if (removedTournamentWithValue) {
        delete next["promo_match_ids"];
        setActiveSportsbookKeys((prev) => {
          const updated = new Set(prev);
          updated.delete("promo_match_ids");
          return updated;
        });
      }
      form.setFieldValue(["steps", stepFieldName, "result_params"], next);
      setActiveSportsbookKeys((prev) => {
        const updated = new Set(prev);
        updated.delete(key);
        return updated;
      });
      if (editingKey === key) setEditingKey(null);
      void revalidatePositiveAmountCrossFields();
    },
    [form, stepFieldName, editingKey, revalidatePositiveAmountCrossFields],
  );

  const finishEditing = async () => {
    if (!editingKey) return;
    const path = ["steps", stepFieldName, "result_params", editingKey] as const;
    try {
      await form.validateFields([path as unknown as (string | number)[]]);
    } catch {
      return;
    }
    const value = form.getFieldValue(path);
    if (!hasParamValue(value)) {
      clearParam(editingKey);
      return;
    }
    setEditingKey(null);
    await revalidatePositiveAmountCrossFields();
  };

  useEffect(() => {
    // Cross-field feedback for the field being edited is shown on the editor
    // itself — only revalidate committed chips once editing finishes.
    if (editingKey) return;
    void revalidatePositiveAmountCrossFields();
  }, [
    editingKey,
    revalidatePositiveAmountCrossFields,
    resultParams.min_bet_amount,
    resultParams.max_wager_limit,
    resultParams.amount,
    resultParams.split_count,
    resultParams.percent_of_trigger,
  ]);

  const menuItems: MenuProps["items"] = availableParams.map((param) => ({
    key: param.key,
    label: param.label,
    onClick: () => {
      if (param.sportsbookSearchType) {
        setActiveSportsbookKeys((prev) => {
          const next = new Set(prev).add(param.key);
          if (param.key === "promo_match_ids") {
            next.add("promo_tournament_ids");
          }
          return next;
        });
      } else {
        setEditingKey(param.key);
      }
    },
  }));

  const paramDependencies = (param: ResultParamField) => {
    const deps: (string | number)[][] = [];
    if (positiveAmountContext) {
      deps.push(
        ["steps", stepFieldName, "result_params", "amount"],
        ["steps", stepFieldName, "result_params", "split_count"],
        ["steps", stepFieldName, "result_params", "max_wager_limit"],
        ["steps", stepFieldName, "result_params", "min_bet_amount"],
        ["steps", stepFieldName, "result_params", "percent_of_trigger"],
      );
    }
    if (param.requiredWhen) {
      deps.push([
        "steps",
        stepFieldName,
        "result_params",
        param.requiredWhen.key,
      ]);
    }
    if (param.visibleWhen) {
      deps.push([
        "steps",
        stepFieldName,
        "result_params",
        param.visibleWhen.key,
      ]);
    }
    return deps.length > 0 ? deps : undefined;
  };

  const renderParamInput = (param: ResultParamField) =>
    param.type === "select" ? (
      <Select
        placeholder={formatString(AppStrings.PAM_DYNAMIC_BONUS_SELECT_FIELD, {
          title: param.label,
        })}
        options={param.options}
      />
    ) : (
      <Input placeholder={param.placeholder} />
    );

  const renderSportsbookField = (
    param: ResultParamField,
    extras: {
      firstTournamentId?: string;
      tournamentCount?: number;
    } = {},
  ) => {
    const companionTournament =
      param.key === "promo_tournament_ids" &&
      matchIsShowing &&
      !hasParamValue(resultParams.promo_tournament_ids);
    return (
      <SportsbookRestrictionSearchField
        key={param.key}
        form={form}
        stepFieldName={stepFieldName}
        paramKey={param.key}
        label={param.label}
        placeholder={param.placeholder}
        searchType={param.sportsbookSearchType!}
        currentCSV={resultParams[param.key] as string | undefined}
        onRemove={companionTournament ? undefined : () => clearParam(param.key)}
        onRestrictionDisplayName={onRestrictionDisplayName}
        {...extras}
      />
    );
  };

  const tournamentParam = activeSportsbookParams.find(
    (param) => param.sportsbookSearchType === "tournament",
  );
  const matchParam = activeSportsbookParams.find(
    (param) => param.sportsbookSearchType === "match",
  );
  const otherSportsbookParams = activeSportsbookParams.filter(
    (param) =>
      param.sportsbookSearchType !== "tournament" &&
      param.sportsbookSearchType !== "match",
  );
  const pairedTournamentAndMatch = Boolean(tournamentParam && matchParam);

  if (params.length === 0) return null;

  return (
    <div
      className={styles.showBonusTargeting}
      data-testid="show-bonus-targeting"
    >
      {setParams.length > 0 && (
        <>
          <div className={styles.showBonusTargetingChips} role="list">
            {setParams.map((param) => {
              if (param.key === editingKey) return null;
              const value = String(resultParams[param.key] ?? "");
              return (
                <span
                  key={param.key}
                  className={styles.showBonusTargetingChip}
                  role="listitem"
                  data-testid={`show-bonus-chip-${param.key}`}
                >
                  <span className={styles.showBonusTargetingChipLabel}>
                    {formatString(AppStrings.PAM_DYNAMIC_BONUS_TARGETING_CONDITION_CHIP, {
                      label: param.label,
                      value,
                    })}
                  </span>
                  <button
                    type="button"
                    className={styles.showBonusTargetingChipRemove}
                    aria-label={formatString(
                      AppStrings.PAM_DYNAMIC_BONUS_REMOVE_TARGETING_CONDITION,
                      { label: param.label },
                    )}
                    onClick={() => clearParam(param.key)}
                  >
                    <CloseOutlined />
                  </button>
                </span>
              );
            })}
          </div>
          <div
            className={styles.showBonusTargetingChipErrors}
            data-testid="show-bonus-chip-errors"
          >
            {setParams.map((param) => {
              if (param.key === editingKey) return null;
              const hideCounterpartChipError =
                (editingKey === "max_wager_limit" && param.key === "min_bet_amount") ||
                (editingKey === "min_bet_amount" && param.key === "max_wager_limit");
              if (hideCounterpartChipError) return null;
              return (
                <Form.Item
                  key={param.key}
                  name={[stepFieldName, "result_params", param.key]}
                  validateTrigger={["onChange", "onBlur"]}
                  rules={paramFieldRules(
                    param,
                    percentContext,
                    positiveAmountContext,
                    resultParams,
                  )}
                  dependencies={paramDependencies(param)}
                  className={styles.showBonusTargetingChipErrorField}
                >
                  <Input aria-hidden tabIndex={-1} />
                </Form.Item>
              );
            })}
          </div>
        </>
      )}

      {editingParam && (
        <div
          className={styles.showBonusTargetingEditor}
          data-testid="show-bonus-targeting-editor"
        >
          <Form.Item
            label={editingParam.label}
            name={[stepFieldName, "result_params", editingParam.key]}
            validateTrigger={["onChange", "onBlur"]}
            rules={paramFieldRules(
              editingParam,
              percentContext,
              positiveAmountContext,
              resultParams,
            )}
            tooltip={editingParam.help}
            dependencies={paramDependencies(editingParam)}
            style={{ marginBottom: 0, flex: 1 }}
          >
            {renderParamInput(editingParam)}
          </Form.Item>
          <Button intent="primary" onClick={finishEditing}>
            {AppStrings.PAM_DYNAMIC_BONUS_TARGETING_CONDITION_DONE}
          </Button>
        </div>
      )}

      {/* BONENG-980: Sportsbook search fields — shown when added via + Add Restriction */}
      {pairedTournamentAndMatch ? (
        <div
          className={styles.sportsbookRestrictionFlow}
          data-testid="sportsbook-restriction-flow"
        >
          <p className={styles.sportsbookRestrictionFlowHeading}>
            {AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_FLOW_HEADING}
          </p>
          {renderSportsbookField(tournamentParam!)}
          {renderSportsbookField(matchParam!, {
            firstTournamentId,
            tournamentCount,
          })}
        </div>
      ) : (
        <>
          {tournamentParam && renderSportsbookField(tournamentParam)}
          {matchParam &&
            renderSportsbookField(matchParam, {
              firstTournamentId,
              tournamentCount,
            })}
        </>
      )}
      {otherSportsbookParams.map((param) => renderSportsbookField(param))}

      {availableParams.length > 0 && (
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button
            intent="neutral"
            variant="ghost"
            iconLeft={<PlusOutlined />}
            className={styles.rulesAddAction}
            data-testid="show-bonus-add-condition"
          >
            {AppStrings.PAM_DYNAMIC_BONUS_ADD_RESTRICTION}
          </Button>
        </Dropdown>
      )}
    </div>
  );
};

export default ShowBonusTargetingField;
